export async function getCountries(token: string): Promise<string[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/countries`, {
    headers: buildAuthHeaders(token),
  });
  const data = await parseResponse<{ countries: string[] }>(response);
  return data.countries;
}
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';

export interface AdminActivity {
  id: number;
  admin_id: number;
  document_id?: number | null;
  action_type: 'upload' | 'update' | 'delete';
  document_name?: string | null;
  created_at: string;
  admin_name?: string | null;
}

export interface AdminDocument {
  id: number;
  original_filename: string;
  stored_name?: string | null;
  document_type?: string | null;
  jurisdiction?: string | null;
  content_type?: string | null;
  file_size?: number | null;
  description?: string | null;
  tags?: string | null;
  created_at: string;
  updated_at?: string | null;
  download_url: string;
  admin_name?: string | null;
  updated_by_admin_name?: string | null;
  admin_id: number;
}

export interface Jurisdiction {
  id: number;
  name: string;
}

export interface AdminYoutubeTranscript {
  id: number;
  youtube_url: string;
  title?: string | null;
  download_url: string;
  language?: string | null;
  created_at: string;
  admin_name?: string | null;
  admin_id: number;
}

export interface AdminYoutubeTranscriptPreview {
  youtube_url: string;
  title?: string | null;
  transcript_text: string;
  language?: string | null;
}

export interface AdminDashboardStats {
  users: {
    users: number;
    admins: number;
  };
  jurisdictions: {
    total: number;
  };
}

export interface UserEngagementMetricsData {
  daily_active_users: { value: number; trend: number };
  avg_session_duration: { value: string; trend: number };
  searches_per_user: { value: number; trend: number };
  admin: {
    active_admins: { value: number; trend: number };
    avg_session_duration: { value: string; trend: number };
    total_actions: { value: number; trend: number };
    actions_per_admin: { value: number; trend: number };
  };
}

export interface AdminUserSummary {
  id: number;
  email: string;
  full_name?: string | null;
  country?: string | null;
  state?: string | null;
  postal_code?: string | null;
  organization_name?: string | null;
  industry?: string | null;
  organization_type?: string | null;
  job_role?: string | null;
  primary_jurisdictions?: string | null;
  regulation_types?: string | null;
  use_cases?: string | null;
  team_size?: string | null;
  plan_tier?: string | null;
  billing_cycle?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  subscription_status?: string | null;
  trial_ends_at?: string | null;
  seats?: number | null;
  amount_paid?: number | null;
  amount_pending?: number | null;
  admin_notes?: string | null;
  created_at?: string | null;
  is_active?: boolean;
}

export interface AdminSummary {
  id: number;
  email: string;
  full_name?: string | null;
  cognito_username: string;
  is_active: boolean;
  created_at: string;
  user_id?: number | null;
  custom_role_id?: number | null;
  role_name?: string | null;
  allowed_pages?: string[];
  allowed_features?: string[];
  allowed_actions?: string[];
  last_login_at?: string | null;    // most recent sign-in; null = never
}

export interface AdminCustomRole {
  id: number;
  name: string;
  description?: string | null;
  pages: string[];
  features: string[];
  actions: string[];
  created_by_admin_id: number;
  created_at: string;
  updated_at: string;
}

export interface AdminCustomRolePayload {
  name: string;
  description?: string;
  pages: string[];
  features: string[];
  actions: string[];
}

interface ApiError extends Error {
  status?: number;
}

const buildAuthHeaders = (token: string, extraHeaders?: HeadersInit): HeadersInit => ({
  Authorization: `Bearer ${token}`,
  ...(extraHeaders || {}),
});

const parseResponse = async <T>(response: Response): Promise<T> => {
  if (response.status === 204) {
    return undefined as T;
  }

  let data: any = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const detail = data?.detail;
    const error: ApiError = new Error(typeof detail === 'string' ? detail : detail?.message || 'Request failed');
    error.status = response.status;
    throw error;
  }

  return data as T;
};

export async function getAdminDashboardStats(token: string): Promise<AdminDashboardStats> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/dashboard/stats`, {
    headers: buildAuthHeaders(token),
  });
  return parseResponse<AdminDashboardStats>(response);
}

export async function getUserEngagementMetrics(token: string): Promise<UserEngagementMetricsData> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/dashboard/user-engagement`, {
    headers: buildAuthHeaders(token),
  });
  return parseResponse<UserEngagementMetricsData>(response);
}

export interface FeedbackStats {
  up: number;
  down: number;
  total: number;
  helpful_pct: number;
}

export async function getFeedbackStats(token: string): Promise<FeedbackStats> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/dashboard/feedback-stats`, {
    headers: buildAuthHeaders(token),
  });
  return parseResponse<FeedbackStats>(response);
}

export interface DataSyncSource {
  source_system: string;
  artifact_count: number;
  last_seen: string | null;
  first_seen: string | null;
  stale: boolean;
  last_run_status: string | null;
  last_run_at: string | null;
  last_run_succeeded: number | null;
  last_run_failed: number | null;
  can_run: boolean;
  // SRS #89–#93
  has_pipeline: boolean;
  env_enabled: boolean | null;
  admin_disabled: boolean;
  enabled: boolean;
  state: 'active' | 'stale' | 'error' | 'never_run' | 'disabled' | 'no_pipeline';
  expected_cadence_hours: number;
  last_error: string | null;
  last_successful_sync_at: string | null;
  // When the source was last polled, whatever the answer. A run that found
  // nothing new keeps this fresh while last_successful_sync_at stays put.
  last_checked_at: string | null;
  failures_24h: number;
  open_alerts: string[];
}

export interface IngestionRunRow {
  run_id: string; status: string | null; started_at: string | null; finished_at: string | null;
  duration_seconds: number | null; attempted: number | null; succeeded: number | null; failed: number | null;
  error_message: string | null; params: string | null;
}
export async function getSourceRuns(token: string, source: string, limit = 25) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/data-sync-status/${encodeURIComponent(source)}/runs?limit=${limit}`, { headers: buildAuthHeaders(token) });
  return parseResponse<{ source: string; runs: IngestionRunRow[] }>(r);
}
export async function setSourceEnabled(token: string, source: string, enabled: boolean) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/data-sync-status/${encodeURIComponent(source)}`, {
    method: 'PATCH', headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }), body: JSON.stringify({ enabled }) });
  return parseResponse<{ source: string; enabled: boolean; disabled_sources: string[] }>(r);
}
export interface SyncAlertRow { id: number; source_system: string; kind: string; message: string; created_at: string | null; resolved_at: string | null; notified: boolean }
export async function getSyncAlerts(token: string, includeResolved = false) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/data-sync-alerts?include_resolved=${includeResolved}`, { headers: buildAuthHeaders(token) });
  return parseResponse<{ alerts: SyncAlertRow[] }>(r);
}
export async function runSyncCheck(token: string) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/data-sync-alerts/check`, { method: 'POST', headers: buildAuthHeaders(token) });
  return parseResponse<{ checked: number; opened: { source: string; kind: string }[]; resolved: { source: string; kind: string }[] }>(r);
}

// What a custom role can be given. Served by the API rather than duplicated
// here: the two lists drifted, and a page the editor offered but the validator
// did not know was discarded on save with nothing shown to whoever ticked it.
export interface RolePermissionOption { key: string; label: string; description: string }
export interface RolePermissionGroup { group: string; description: string; items: RolePermissionOption[] }
export interface RoleCatalogue {
  pages: RolePermissionGroup[];
  features: RolePermissionGroup[];
  actions: RolePermissionGroup[];
}
export async function getRoleCatalogue(token: string): Promise<RoleCatalogue> {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/custom-roles/catalogue`, {
    headers: buildAuthHeaders(token),
  });
  return parseResponse<RoleCatalogue>(r);
}

// Adding and correcting regulations by hand. Nothing in the product could do
// this: the seeded jurisdictions were inserted by a script against the
// database, and a wrong title took a deployment to fix.
export interface ManualRegulationInput {
  jurisdiction: string;
  title: string;
  document_type?: string;
  document_no?: string;
  status?: string;
  source_url?: string;
  summary?: string;
  effective_date?: string;
  last_action_date?: string;
}
export interface ManualRowResult {
  row: number;
  action: 'create' | 'update' | 'error';
  title: string;
  jurisdiction: string;
  artifact_id: string | null;
  error: string | null;
  changes: Record<string, unknown>;
}
export interface ManualImportResult {
  dry_run: boolean;
  committed: boolean;
  tally: { created: number; updated: number; errors: number; rows: number };
  rows: ManualRowResult[];
}
export async function addRegulation(token: string, input: ManualRegulationInput): Promise<ManualRowResult> {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/regulations`, {
    method: 'POST', headers: { ...buildAuthHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return parseResponse<ManualRowResult>(r);
}
export async function importRegulations(token: string, csv: string, dryRun: boolean): Promise<ManualImportResult> {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/regulations/import`, {
    method: 'POST', headers: { ...buildAuthHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ csv, dry_run: dryRun }),
  });
  return parseResponse<ManualImportResult>(r);
}

// Corpus inventory: why the artifact count and the published regulation count
// differ, record by record. Read-only; admin only.
export type CorpusBucket = 'published' | 'screened' | 'unassessed' | 'missing_text';
export interface CorpusBucketCounts {
  total: number;
  linked: number;
  published: number;
  screened: number;
  unassessed: number;
  missing_text: number;
}
export interface CorpusSourceRow extends CorpusBucketCounts {
  source_system: string;
  // The site most of this source's records come from, taken from the records
  // themselves rather than a list kept by hand. Null when none carry a link.
  publisher: string | null;
}
export interface CorpusRecord {
  id: string;
  title: string;
  jurisdiction: string | null;
  jurisdiction_code: string | null;
  regulation_type: string | null;
  source_system: string;
  branch: string | null;
  relevance_status: string | null;
  text_chars: number;
  source_url: string | null;
  first_seen_at: string | null;
  last_action_date: string | null;
  bucket: CorpusBucket;
}
export interface CorpusInventory {
  floor_chars: number;
  labels: Record<CorpusBucket, string>;
  totals: CorpusBucketCounts;
  by_source: CorpusSourceRow[];
  listing: {
    total: number;
    limit: number;
    offset: number;
    bucket: CorpusBucket | null;
    source_system: string | null;
    items: CorpusRecord[];
  };
}
export async function getCorpusInventory(
  token: string,
  opts: { bucket?: CorpusBucket; sourceSystem?: string; limit?: number; offset?: number } = {},
): Promise<CorpusInventory> {
  const q = new URLSearchParams();
  if (opts.bucket) q.set('bucket', opts.bucket);
  if (opts.sourceSystem) q.set('source_system', opts.sourceSystem);
  if (opts.limit != null) q.set('limit', String(opts.limit));
  if (opts.offset != null) q.set('offset', String(opts.offset));
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/corpus-inventory?${q.toString()}`, {
    headers: buildAuthHeaders(token),
  });
  return parseResponse<CorpusInventory>(r);
}

export async function getDataSyncStatus(
  token: string,
): Promise<{ sources: DataSyncSource[]; generated_at: string }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/data-sync-status`, {
    headers: buildAuthHeaders(token),
  });
  return parseResponse<{ sources: DataSyncSource[]; generated_at: string }>(response);
}

// Manually enqueue a source's ingestion pipeline ("Run now"). Fire-and-forget:
// the run shows up in getDataSyncStatus once the worker picks it up.
export async function triggerIngestionRun(
  token: string,
  source: string,
): Promise<{ queued: boolean; source: string; task: string; task_id: string | null }> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/admin/data-sync-status/${encodeURIComponent(source)}/run`,
    { method: 'POST', headers: buildAuthHeaders(token) },
  );
  return parseResponse(response);
}

// ── News noise cleanup ───────────────────────────────────────────────────────
// Lets an admin remove an off-topic article that slipped past the scraper's
// relevance filter (the filter only screens NEW scrapes, so already-stored
// noise needs a manual removal path).
export async function deleteNewsArticle(token: string, articleId: string): Promise<{ deleted: boolean; id: string; title: string }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/news/${encodeURIComponent(articleId)}`, {
    method: 'DELETE',
    headers: buildAuthHeaders(token),
  });
  return parseResponse(response);
}

// ── Admin pricing editor (Phase-1 #7) ────────────────────────────────────────
export interface AdminPlanPricing {
  tier: string;
  display_name: string;
  monthly_price_usd: string | null;
  annual_price_usd: string | null;
  annual_monthly_equivalent: string | null;
  annual_savings_pct: number;
  contact_sales: boolean;
  overridden: boolean;
  disabled: boolean;
}

export async function getAdminPricing(token: string): Promise<AdminPlanPricing[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/pricing/admin`, {
    headers: buildAuthHeaders(token),
  });
  return parseResponse<AdminPlanPricing[]>(response);
}

export async function setAdminPricing(
  token: string,
  tier: string,
  body: { monthly_price_usd?: number | null; annual_price_usd?: number | null; disabled?: boolean },
): Promise<unknown> {
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/pricing/admin/${tier}`, {
    method: 'PUT',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  return parseResponse<unknown>(response);
}

export async function resetAdminPricing(token: string, tier: string): Promise<unknown> {
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/pricing/admin/${tier}`, {
    method: 'DELETE',
    headers: buildAuthHeaders(token),
  });
  return parseResponse<unknown>(response);
}

// ── Admin alert matrix (QA Phase-2) ──────────────────────────────────────────
export interface AdminAlert {
  id: string;
  user_email: string | null;
  name: string;
  is_active: boolean;
  frequency: string;
  jurisdictions: string[];
  keywords: string[];
  regulation_types: string[];
  semantic_query: string | null;
  trigger_count: number;
  last_triggered_at: string | null;
  created_at: string | null;
}

export async function getAdminAlerts(token: string): Promise<{ alerts: AdminAlert[]; total: number }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/alerts`, {
    headers: buildAuthHeaders(token),
  });
  return parseResponse<{ alerts: AdminAlert[]; total: number }>(response);
}

export async function getAdminDocuments(token: string): Promise<AdminDocument[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/documents`, {
    headers: buildAuthHeaders(token),
  });
  const data = await parseResponse<{ documents: AdminDocument[] }>(response);
  return data.documents;
}

export async function uploadAdminDocument(
  token: string,
  file: File,
  description?: string,
  metadata?: {
    document_type?: string;
    jurisdiction?: string;
    stored_name?: string;
    tags?: string;
  },
): Promise<AdminDocument> {
  const formData = new FormData();
  formData.append('file', file);
  if (description) {
    formData.append('description', description);
  }
  if (metadata?.document_type) {
    formData.append('document_type', metadata.document_type);
  }
  if (metadata?.jurisdiction) {
    formData.append('jurisdiction', metadata.jurisdiction);
  }
  if (metadata?.stored_name) {
    formData.append('stored_name', metadata.stored_name);
  }
  if (metadata?.tags) {
    formData.append('tags', metadata.tags);
  }
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/documents`, {
    method: 'POST',
    headers: buildAuthHeaders(token),
    body: formData,
  });
  return parseResponse<AdminDocument>(response);
}

export async function getAdminDocumentTypes(token: string): Promise<string[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/document-types`, {
    headers: buildAuthHeaders(token),
  });
  const data = await parseResponse<{ types: string[] }>(response);
  return data.types;
}

export async function deleteAdminDocumentType(token: string, typeName: string): Promise<{ deleted_type: string; documents_updated: number }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/document-types/${encodeURIComponent(typeName)}`, {
    method: 'DELETE',
    headers: buildAuthHeaders(token),
  });
  return parseResponse<{ deleted_type: string; documents_updated: number }>(response);
}

export async function deleteAdminDocument(token: string, documentId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/documents/${documentId}`, {
    method: 'DELETE',
    headers: buildAuthHeaders(token),
  });
  await parseResponse<void>(response);
}

export async function bulkDeleteAdminDocuments(token: string, ids: number[]): Promise<{ deleted: number }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/documents/bulk`, {
    method: 'DELETE',
    headers: { ...buildAuthHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  return parseResponse<{ deleted: number }>(response);
}

export async function updateAdminDocumentName(token: string, documentId: number, storedName: string): Promise<AdminDocument> {
  const formData = new FormData();
  formData.append('stored_name', storedName);
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/documents/${documentId}`, {
    method: 'PATCH',
    headers: buildAuthHeaders(token),
    body: formData,
  });
  return parseResponse<AdminDocument>(response);
}

export async function updateAdminDocument(
  token: string,
  documentId: number,
  updates: {
    stored_name?: string;
    document_type?: string;
    jurisdiction?: string;
    description?: string;
    tags?: string;
  }
): Promise<AdminDocument> {
  const formData = new FormData();
  if (updates.stored_name) {
    formData.append('stored_name', updates.stored_name);
  }
  if (updates.document_type) {
    formData.append('document_type', updates.document_type);
  }
  if (updates.jurisdiction) {
    formData.append('jurisdiction', updates.jurisdiction);
  }
  if (updates.description) {
    formData.append('description', updates.description);
  }
  if (updates.tags !== undefined) {
    formData.append('tags', updates.tags);
  }
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/documents/${documentId}`, {
    method: 'PATCH',
    headers: buildAuthHeaders(token),
    body: formData,
  });
  return parseResponse<AdminDocument>(response);
}

export async function getAdminActivities(token: string, limit: number = 20): Promise<AdminActivity[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/activities?limit=${limit}`, {
    headers: buildAuthHeaders(token),
  });
  const data = await parseResponse<{ activities: AdminActivity[] }>(response);
  return data.activities;
}

export async function getAdminYoutubeTranscripts(token: string): Promise<AdminYoutubeTranscript[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/youtube-transcripts`, {
    headers: buildAuthHeaders(token),
  });
  const data = await parseResponse<{ transcripts: AdminYoutubeTranscript[] }>(response);
  return data.transcripts;
}

export interface S3File {
  key: string;
  filename: string;
  size: number;
  last_modified?: string | null;
  download_url: string;
  prefix: string;
}

export async function getS3Files(token: string): Promise<S3File[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/s3-files`, {
    headers: buildAuthHeaders(token),
  });
  const data = await parseResponse<{ files: S3File[] }>(response);
  return data.files;
}

export interface RegisterS3FilePayload {
  key: string;
  name?: string;
  document_type?: string;
  jurisdiction?: string;
  tags?: string;
}

export async function registerS3File(token: string, payload: RegisterS3FilePayload): Promise<{ id: number; message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/s3-files/register`, {
    method: 'POST',
    headers: { ...buildAuthHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse<{ id: number; message: string }>(response);
}

export async function getAdminJurisdictions(token: string, country?: string): Promise<{ stats: any[]; by_country: Record<string, number> }>{
  const qs = country ? `?country=${encodeURIComponent(country)}` : '';
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/jurisdictions${qs}`, {
    headers: buildAuthHeaders(token),
  });
  return parseResponse<{ stats: any[]; by_country: Record<string, number> }>(response);
}

export async function previewAdminYoutubeTranscript(
  token: string,
  payload: { youtube_url: string; language?: string | null },
): Promise<AdminYoutubeTranscriptPreview> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/youtube-transcripts/preview`, {
    method: 'POST',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  return parseResponse<AdminYoutubeTranscriptPreview>(response);
}

export async function createAdminYoutubeTranscript(
  token: string,
  payload: { youtube_url: string; language?: string | null; transcript_text?: string; detected_language?: string | null },
): Promise<AdminYoutubeTranscript> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/youtube-transcripts`, {
    method: 'POST',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  return parseResponse<AdminYoutubeTranscript>(response);
}

export async function deleteAdminYoutubeTranscript(token: string, transcriptId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/youtube-transcripts/${transcriptId}`, {
    method: 'DELETE',
    headers: buildAuthHeaders(token),
  });
  await parseResponse<void>(response);
}

export async function bulkDeleteAdminTranscripts(token: string, ids: number[]): Promise<{ deleted: number }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/youtube-transcripts/bulk`, {
    method: 'DELETE',
    headers: { ...buildAuthHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  return parseResponse<{ deleted: number }>(response);
}

export async function bulkDeleteS3Files(token: string, keys: string[]): Promise<{ deleted: number }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/s3-files/bulk`, {
    method: 'DELETE',
    headers: { ...buildAuthHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ keys }),
  });
  return parseResponse<{ deleted: number }>(response);
}

export async function getAdminUsers(token: string): Promise<AdminUserSummary[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/users`, {
    headers: buildAuthHeaders(token),
  });
  return parseResponse<AdminUserSummary[]>(response);
}

export interface CreateUserPayload {
  email: string;
  full_name?: string;
  password: string;
  plan_tier?: string;
  organization_name?: string;
  organization_type?: string;
  job_role?: string;
  billing_cycle?: string;
  period_start?: string;
  period_end?: string;
  seats?: number;
  amount_paid?: number;
  amount_pending?: number;
  admin_notes?: string;
}

export interface UpdateUserPayload {
  full_name?: string | null;
  /** false = blocked: login refused and existing sessions end (SRS #78) */
  is_active?: boolean;
  organization_name?: string | null;
  organization_type?: string | null;
  job_role?: string | null;
  plan_tier?: string | null;
  billing_cycle?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  seats?: number | null;
  amount_paid?: number | null;
  amount_pending?: number | null;
  admin_notes?: string | null;
}

export async function createUser(token: string, payload: CreateUserPayload): Promise<AdminUserSummary> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/users`, {
    method: 'POST',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  return parseResponse<AdminUserSummary>(response);
}

export async function cleanupPreviewUsers(token: string): Promise<{ deleted: number; emails: string[] }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/users/cleanup-preview`, {
    method: 'DELETE',
    headers: buildAuthHeaders(token),
  });
  return parseResponse<{ deleted: number; emails: string[] }>(response);
}

export async function updateUser(token: string, userId: number, payload: UpdateUserPayload): Promise<AdminUserSummary> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/users/${userId}`, {
    method: 'PATCH',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  return parseResponse<AdminUserSummary>(response);
}

export async function deleteUser(token: string, userId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/users/${userId}`, {
    method: 'DELETE',
    headers: buildAuthHeaders(token),
  });
  await parseResponse<void>(response);
}

export async function revokeUserAccess(token: string, userId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/users/${userId}/revoke`, {
    method: 'POST',
    headers: buildAuthHeaders(token),
  });
  await parseResponse<void>(response);
}

export interface ResetUserPasswordResult {
  message: string;
  user_id: number;
  email: string;
  email_sent: boolean;
  /** Present only when the server generated the password — shown once, never retrievable again. */
  temporary_password: string | null;
}

/**
 * Reset a user's password on their behalf.
 *
 * Omit `newPassword` to have one generated. Resetting also ends every active
 * session for that account, so anyone signed in as them is logged out.
 */
export async function resetUserPassword(
  token: string,
  userId: number,
  newPassword?: string,
  notifyUser: boolean = true,
): Promise<ResetUserPasswordResult> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/users/${userId}/reset-password`, {
    method: 'POST',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ new_password: newPassword ?? null, notify_user: notifyUser }),
  });
  return parseResponse<ResetUserPasswordResult>(response);
}

export async function grantUserAccess(token: string, userId: number, days: number = 14, planTier?: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/users/${userId}/grant-access`, {
    method: 'POST',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ days, plan_tier: planTier }),
  });
  await parseResponse<void>(response);
}

export async function searchUsers(
  token: string,
  query?: string,
  orgTypes?: string[],
  planTierFilter?: string,
): Promise<AdminUserSummary[]> {
  const params = new URLSearchParams({ limit: '500' });
  if (query) params.append('q', query);
  if (orgTypes && orgTypes.length > 0) params.append('org_types', orgTypes.join(','));
  if (planTierFilter) params.append('plan_tier_filter', planTierFilter);
  const url = `${API_BASE_URL}/api/v1/admin/users?${params.toString()}`;
  const response = await fetch(url, {
    headers: buildAuthHeaders(token),
  });
  return parseResponse<AdminUserSummary[]>(response);
}

export async function getUsersByOrganizationType(
  token: string,
  organizationType: string,
  query?: string
): Promise<AdminUserSummary[]> {
  const users = await searchUsers(token, query);
  // Filter by organization_type if provided
  return users.filter(user => user.organization_type === organizationType);
}

export async function getAdmins(token: string): Promise<AdminSummary[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/admins`, {
    headers: buildAuthHeaders(token),
  });
  return parseResponse<AdminSummary[]>(response);
}

export async function getAdminCustomRoles(token: string): Promise<AdminCustomRole[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/custom-roles`, {
    headers: buildAuthHeaders(token),
  });
  return parseResponse<AdminCustomRole[]>(response);
}

export async function createAdminCustomRole(token: string, payload: AdminCustomRolePayload): Promise<AdminCustomRole> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/custom-roles`, {
    method: 'POST',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  return parseResponse<AdminCustomRole>(response);
}

export async function updateAdminCustomRole(token: string, roleId: number, payload: Partial<AdminCustomRolePayload>): Promise<AdminCustomRole> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/custom-roles/${roleId}`, {
    method: 'PUT',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  return parseResponse<AdminCustomRole>(response);
}

export async function deleteAdminCustomRole(token: string, roleId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/custom-roles/${roleId}`, {
    method: 'DELETE',
    headers: buildAuthHeaders(token),
  });
  await parseResponse<void>(response);
}

export interface AdminCreatePayload {
  email: string;
  full_name?: string;
  password: string;
  custom_role_id?: number | null;
}

export interface AdminUpdatePayload {
  is_active?: boolean;
  custom_role_id?: number | null;   // 0 or null = default full access
  full_name?: string;
  email?: string;
}

export async function createAdmin(token: string, payload: AdminCreatePayload): Promise<AdminSummary> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/admins`, {
    method: 'POST',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  return parseResponse<AdminSummary>(response);
}

export async function sendAdminCredentials(token: string, payload: AdminCreatePayload): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/admins/send-credentials`, {
    method: 'POST',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  await parseResponse(response);
}

export async function updateAdmin(token: string, adminId: string, payload: AdminUpdatePayload): Promise<AdminSummary> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/admins/${adminId}`, {
    method: 'PATCH',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  return parseResponse<AdminSummary>(response);
}

export async function updateAdminStatus(token: string, adminId: string, isActive: boolean): Promise<AdminSummary> {
  return updateAdmin(token, adminId, { is_active: isActive });
}

/** Delete a revoked admin; its documents, reports, transcripts and roles pass to the caller. */
export async function removeAdminAccount(token: string, adminId: string): Promise<{ handed_over: Record<string, number> }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/admins/${adminId}`, {
    method: 'DELETE',
    headers: buildAuthHeaders(token),
  });
  return parseResponse<{ handed_over: Record<string, number> }>(response);
}

export async function revokeAdminAccess(token: string, adminId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/admins/${adminId}/revoke`, {
    method: 'POST',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
  });
  await parseResponse<void>(response);
}
export async function getJurisdictions(token: string): Promise<Jurisdiction[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/search/jurisdictions`, {
      headers: buildAuthHeaders(token),
    });
    console.log('Jurisdictions API response status:', response.status);
    const data = await parseResponse<any>(response);
    console.log('Jurisdictions API response data:', data);
    
    // Handle different response formats
    if (Array.isArray(data)) {
      console.log('Returned array format, count:', data.length);
      return data;
    }
    if (data?.jurisdictions && Array.isArray(data.jurisdictions)) {
      console.log('Returned jurisdictions field, count:', data.jurisdictions.length);
      return data.jurisdictions;
    }
    if (data?.data && Array.isArray(data.data)) {
      console.log('Returned data field, count:', data.data.length);
      return data.data;
    }
    
    console.warn('Unexpected jurisdictions response format:', data);
    return [];
  } catch (error) {
    console.error('Error fetching jurisdictions:', error);
    return [];
  }
}

// ── Admin: audit log, security, login events, search config, plan matrix ──────

const q = (params: Record<string, string | number | boolean | undefined | null>) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') sp.append(k, String(v)); });
  const str = sp.toString();
  return str ? `?${str}` : '';
};

export interface AuditEntry {
  id: number; admin_id: number | null; admin_email: string; action: string; target_type: string | null;
  target_id: string | null; target_label: string | null; detail: Record<string, unknown> | null; ip_address: string | null; created_at: string;
}
export async function getAuditLog(token: string, params: { action?: string; admin_email?: string; target_type?: string; target_id?: string; since?: string; limit?: number; offset?: number }) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/audit-log${q(params)}`, { headers: buildAuthHeaders(token) });
  // The audit-log endpoint (older) answers `entries`; the newer security endpoints answer `items`. Normalise here
  // so the page never meets `undefined.map` (it crashed blank on the live site, 19 Sep).
  const data = await parseResponse<{ items?: AuditEntry[]; entries?: AuditEntry[]; total: number; limit: number; offset: number }>(r);
  return { ...data, items: data.items ?? data.entries ?? [] };
}

export interface SecurityEvent { id: number; kind: string; ip_address: string | null; path: string | null; detail: Record<string, unknown> | null; created_at: string }
export async function getSecurityEvents(token: string, params: { kind?: string; ip?: string; since?: string; limit?: number; offset?: number }) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/security-events${q(params)}`, { headers: buildAuthHeaders(token) });
  return parseResponse<{ items: SecurityEvent[]; total: number }>(r);
}

export interface BlockedIP { id: number; network: string; reason: string; source: string; created_by: string | null; created_at: string; expires_at: string | null; hits: number; last_hit_at: string | null }
export async function getBlockedIPs(token: string, includeExpired = false) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/blocked-ips${q({ include_expired: includeExpired })}`, { headers: buildAuthHeaders(token) });
  return parseResponse<{ items: BlockedIP[]; total: number }>(r);
}
export async function blockIP(token: string, body: { network: string; reason: string; hours?: number | null }) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/blocked-ips`, { method: 'POST', headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }), body: JSON.stringify(body) });
  return parseResponse<BlockedIP>(r);
}
export async function unblockIP(token: string, id: number) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/blocked-ips/${id}`, { method: 'DELETE', headers: buildAuthHeaders(token) });
  if (!r.ok) throw new Error(`Unblock failed (${r.status})`);
}

export interface LoginEvent { id: number; email: string | null; login_at: string; logout_at: string | null; ip_address: string | null; device: string | null; user_agent: string | null; method: string | null }
export async function getLoginEvents(token: string, params: { email?: string; ip?: string; since?: string; limit?: number; offset?: number }) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/login-events${q(params)}`, { headers: buildAuthHeaders(token) });
  return parseResponse<{ items: LoginEvent[]; total: number }>(r);
}

export interface SearchConfigValues { min_relevance: number; semantic_enabled: boolean; semantic_weight: number; semantic_min_similarity: number; excluded_sources: string[]; excluded_jurisdictions: string[]; version: string }
export interface SearchConfigView { effective: SearchConfigValues; defaults: SearchConfigValues; override: Partial<SearchConfigValues> | null; updated_at: string | null; updated_by: string | null; embeddings: number; semantic_possible: boolean }
export async function getSearchConfig(token: string) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/search-config`, { headers: buildAuthHeaders(token) });
  return parseResponse<SearchConfigView>(r);
}
export async function updateSearchConfig(token: string, body: Partial<Omit<SearchConfigValues, 'version'>>) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/search-config`, { method: 'PUT', headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }), body: JSON.stringify(body) });
  return parseResponse<SearchConfigView>(r);
}
export async function resetSearchConfig(token: string) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/search-config`, { method: 'DELETE', headers: buildAuthHeaders(token) });
  return parseResponse<SearchConfigView>(r);
}
export async function getSearchSources(token: string) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/search-config/sources`, { headers: buildAuthHeaders(token) });
  return parseResponse<{ sources: { source: string; total: number }[]; jurisdictions: { name: string; code: string | null; total: number }[] }>(r);
}

export interface PlanMatrixRow { tier: string; display_name: string; disabled: boolean; quantities: Record<string, number | string | null>; advertised: Record<string, boolean>; enforced: Record<string, boolean> }
export interface PlanMatrixView { features: string[]; quantity_fields: string[]; plans: PlanMatrixRow[]; issues?: { kind: string; tier: string; detail: string }[] }
export async function getPlanMatrix(token: string) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/plan-matrix`, { headers: buildAuthHeaders(token) });
  return parseResponse<PlanMatrixView>(r);
}

// ── Authorised jurisdictions per account (SRS #59) ────────────────────────────
export interface AuthorisedJurisdictionsView { user_id: number; email: string; authorised_jurisdictions: string[] | null; is_government: boolean; effective: string[] | null }
export async function getAuthorisedJurisdictions(token: string, userId: number) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/users/${userId}/authorised-jurisdictions`, { headers: buildAuthHeaders(token) });
  return parseResponse<AuthorisedJurisdictionsView>(r);
}
export async function setAuthorisedJurisdictions(token: string, userId: number, jurisdictions: string[]) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/users/${userId}/authorised-jurisdictions`, {
    method: 'PUT', headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }), body: JSON.stringify({ jurisdictions }) });
  return parseResponse<{ user_id: number; authorised_jurisdictions: string[] | null; effective: string[] | null }>(r);
}

// ── Data quality (SRS #115–#119) ─────────────────────────────────────────────
export interface DQFinding { id: number; artifact_id: string; source_system: string | null; jurisdiction_name: string | null; title: string | null; area: 'source' | 'reference' | 'status'; kind: string; severity: 'high' | 'medium' | 'low'; detail: string | null; first_seen_at: string; last_seen_at: string; resolved_at: string | null; dismissed_by: string | null }
export interface DQRun { id: number; started_at: string; finished_at: string | null; scanned: number; open_findings: number; new_findings: number; resolved_findings: number; summary: Record<string, any> | null; notified: boolean }
export interface DQSummary { last_run: DQRun | null; open: { total: number; by_kind: Record<string, number>; by_source: Record<string, number>; by_area: Record<string, number>; by_severity: Record<string, number> } }
export async function getDataQualitySummary(token: string) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/data-quality/summary`, { headers: buildAuthHeaders(token) });
  return parseResponse<DQSummary>(r);
}
export async function getDataQualityFindings(token: string, params: { kind?: string; area?: string; source?: string; severity?: string; include_resolved?: boolean; limit?: number; offset?: number }) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/data-quality/findings${q(params)}`, { headers: buildAuthHeaders(token) });
  return parseResponse<{ items: DQFinding[]; total: number }>(r);
}
export async function runDataQuality(token: string) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/data-quality/run`, { method: 'POST', headers: buildAuthHeaders(token) });
  return parseResponse<{ run_id: number; scanned: number; open_findings: number; new_findings: number; resolved_findings: number }>(r);
}
export async function dismissDataQualityFinding(token: string, id: number) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/data-quality/findings/${id}/dismiss`, { method: 'POST', headers: buildAuthHeaders(token) });
  return parseResponse<{ id: number }>(r);
}
export async function getStatusVocab(token: string) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/data-quality/status-vocab`, { headers: buildAuthHeaders(token) });
  return parseResponse<{ builtin: Record<string, string>; learned: Record<string, string>; unknown: { status: string; count: number }[] }>(r);
}
export async function classifyStatuses(token: string) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/data-quality/status-vocab/classify`, { method: 'POST', headers: buildAuthHeaders(token) });
  return parseResponse<{ learned: Record<string, string> }>(r);
}

// ── Data coverage acceptance (SRS #167) ──────────────────────────────────────
export interface CoverageCriterion { claim: string; verdict: 'pass' | 'fail' | 'attention'; note: string; [k: string]: any }
export interface CoverageReport { generated_at: string; instruments: number; overall: 'pass' | 'fail' | 'attention'; criteria: CoverageCriterion[]; needs_from_kn: string[] }
export async function getDataCoverage(token: string) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/data-coverage`, { headers: buildAuthHeaders(token) });
  return parseResponse<CoverageReport>(r);
}
/** Download the jurisdictions table (name, code, country, regulation count, newest regulation) as CSV. */
export async function downloadJurisdictionsCsv(token: string, country?: string) {
  const qs = country ? `?country=${encodeURIComponent(country)}` : '';
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/jurisdictions/export.csv${qs}`, { headers: buildAuthHeaders(token) });
  if (!r.ok) throw new Error(`Export failed (${r.status})`);
  const href = URL.createObjectURL(await r.blob());
  const a = document.createElement('a'); a.href = href; a.download = 'policyora-jurisdictions.csv';
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(href);
}

export async function downloadDataCoverageMarkdown(token: string) {
  const r = await fetch(`${API_BASE_URL}/api/v1/admin/data-coverage?format=md`, { headers: buildAuthHeaders(token) });
  if (!r.ok) throw new Error(`Export failed (${r.status})`);
  const href = URL.createObjectURL(await r.blob());
  const a = document.createElement('a'); a.href = href; a.download = 'policyora-data-coverage.md';
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(href);
}
